import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { BookOpen, Plus, Upload, ChevronDown, ChevronRight, Check, Clock, FileText } from 'lucide-react'

interface SpellingList {
  id: number
  name: string
  word_count: number
  is_active: boolean
  assigned_at: string
  word_preview: string[]
}

interface Student {
  id: number
  name: string
}

export default function ParentSpellingLists() {
  const queryClient = useQueryClient()
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null)
  const [showNewForm, setShowNewForm] = useState(false)
  const [listName, setListName] = useState('')
  const [wordsText, setWordsText] = useState('')
  const [expandedListId, setExpandedListId] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: studentsData } = useQuery<{ students: Student[] }>({
    queryKey: ['parent-students'],
    queryFn: async () => {
      const response = await api.get('/api/parent/students')
      return response.data
    },
  })

  const students = studentsData?.students || []

  // Auto-select first student
  if (!selectedStudentId && students.length > 0) {
    setSelectedStudentId(students[0].id)
  }

  const { data: listsData, isLoading: listsLoading } = useQuery<{ lists: SpellingList[] }>({
    queryKey: ['spelling-lists', selectedStudentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${selectedStudentId}/spelling-lists`)
      return response.data
    },
    enabled: !!selectedStudentId,
  })

  const lists = listsData?.lists || []
  const activeList = lists.find(l => l.is_active)
  const archivedLists = lists.filter(l => !l.is_active)

  const createMutation = useMutation({
    mutationFn: async ({ words, name }: { words: string[], name: string }) => {
      const response = await api.post(`/api/parent/students/${selectedStudentId}/spelling-lists`, {
        words,
        name,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spelling-lists', selectedStudentId] })
      setShowNewForm(false)
      setListName('')
      setWordsText('')
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async ({ file, name }: { file: File, name: string }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('name', name)
      const response = await api.post(
        `/api/parent/students/${selectedStudentId}/spelling-lists/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spelling-lists', selectedStudentId] })
      setShowNewForm(false)
      setListName('')
      setWordsText('')
    },
  })

  const parseWordsPreview = (text: string): string[] => {
    const words: string[] = []
    const seen = new Set<string>()
    for (const line of text.split('\n')) {
      const trimmed = line.trim()
      if (!trimmed) continue
      const parts = trimmed.includes(',') ? trimmed.split(',') : [trimmed]
      for (const part of parts) {
        const word = part.trim().toLowerCase()
        if (word && !seen.has(word)) {
          seen.add(word)
          words.push(word)
        }
      }
    }
    return words
  }

  const parsedWords = parseWordsPreview(wordsText)

  const defaultName = () => {
    const now = new Date()
    const weekNum = Math.ceil((now.getDate()) / 7)
    return `Week ${weekNum} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
  }

  const handleSubmit = () => {
    if (parsedWords.length === 0) return
    createMutation.mutate({
      words: parsedWords,
      name: listName || defaultName(),
    })
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    uploadMutation.mutate({
      file,
      name: listName || `Uploaded: ${file.name}`,
    })
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    })
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-quest-primary" />
            Spelling Lists
          </h1>
          <p className="text-gray-400 mt-1">
            Manage weekly spelling words for your children
          </p>
        </div>
        {selectedStudentId && (
          <button
            onClick={() => setShowNewForm(!showNewForm)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Word List
          </button>
        )}
      </div>

      {/* Student Selector */}
      {students.length > 1 && (
        <div className="flex gap-2">
          {students.map(student => (
            <button
              key={student.id}
              onClick={() => {
                setSelectedStudentId(student.id)
                setShowNewForm(false)
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedStudentId === student.id
                  ? 'bg-quest-primary text-white'
                  : 'bg-surface-card text-gray-300 hover:bg-surface-hover'
              }`}
            >
              {student.name}
            </button>
          ))}
        </div>
      )}

      {/* New Word List Form */}
      {showNewForm && (
        <div className="card p-6 space-y-4">
          <h2 className="text-xl font-bold">New Word List</h2>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              List Name
            </label>
            <input
              type="text"
              value={listName}
              onChange={e => setListName(e.target.value)}
              placeholder={defaultName()}
              className="w-full bg-surface-card border border-gray-700/50 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-quest-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Words (one per line, or comma-separated)
            </label>
            <textarea
              value={wordsText}
              onChange={e => setWordsText(e.target.value)}
              placeholder="apple&#10;banana&#10;cherry&#10;&#10;or: apple, banana, cherry"
              rows={8}
              className="w-full bg-surface-card border border-gray-700/50 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-quest-primary focus:outline-none font-mono text-sm"
            />
          </div>

          {/* Parsed preview */}
          {parsedWords.length > 0 && (
            <div className="bg-surface-card/50 rounded-lg p-3">
              <p className="text-sm text-gray-400 mb-2">
                Preview: {parsedWords.length} word{parsedWords.length !== 1 ? 's' : ''}
              </p>
              <div className="flex flex-wrap gap-1">
                {parsedWords.map((word, i) => (
                  <span key={i} className="px-2 py-0.5 bg-gray-700 rounded text-sm text-gray-200">
                    {word}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={handleSubmit}
              disabled={parsedWords.length === 0 || createMutation.isPending}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Saving...' : 'Save Word List'}
            </button>

            <span className="text-gray-500">or</span>

            <label className="btn bg-gray-700 hover:bg-gray-600 text-white flex items-center gap-2 cursor-pointer">
              <Upload className="w-4 h-4" />
              Upload File
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.csv"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            <button
              onClick={() => {
                setShowNewForm(false)
                setWordsText('')
                setListName('')
              }}
              className="btn bg-surface-card text-gray-300 hover:bg-surface-hover"
            >
              Cancel
            </button>
          </div>

          {(createMutation.isError || uploadMutation.isError) && (
            <p className="text-red-400 text-sm">
              {(createMutation.error as any)?.response?.data?.error
                || (uploadMutation.error as any)?.response?.data?.error
                || 'Failed to save word list'}
            </p>
          )}
        </div>
      )}

      {/* Loading */}
      {listsLoading && (
        <div className="text-gray-400 text-center py-8">Loading spelling lists...</div>
      )}

      {/* Active List */}
      {activeList && (
        <section>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Check className="w-5 h-5 text-green-400" />
            Active List
          </h2>
          <div className="card p-5 border border-green-500/30">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold">{activeList.name}</h3>
                <p className="text-sm text-gray-400 mt-1">
                  {activeList.word_count} words &middot; Assigned {formatDate(activeList.assigned_at)}
                </p>
              </div>
              <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                Active
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {activeList.word_preview.map((word, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-700 rounded text-sm text-gray-200">
                  {word}
                </span>
              ))}
              {activeList.word_count > activeList.word_preview.length && (
                <span className="px-2 py-0.5 text-sm text-gray-500">
                  +{activeList.word_count - activeList.word_preview.length} more
                </span>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {!listsLoading && selectedStudentId && lists.length === 0 && !showNewForm && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-300">No spelling lists yet</h3>
          <p className="text-gray-500 mt-1">
            Create a word list to get started with spelling practice
          </p>
          <button
            onClick={() => setShowNewForm(true)}
            className="btn btn-primary mt-4"
          >
            <Plus className="w-4 h-4 inline mr-1" />
            Create First List
          </button>
        </div>
      )}

      {/* Archived Lists */}
      {archivedLists.length > 0 && (
        <section>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-400" />
            Previous Lists
          </h2>
          <div className="space-y-2">
            {archivedLists.map(list => (
              <div key={list.id} className="card">
                <button
                  onClick={() => setExpandedListId(expandedListId === list.id ? null : list.id)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-surface-card/50 transition-colors"
                >
                  <div>
                    <h3 className="font-medium">{list.name}</h3>
                    <p className="text-sm text-gray-500">
                      {list.word_count} words &middot; {formatDate(list.assigned_at)}
                    </p>
                  </div>
                  {expandedListId === list.id
                    ? <ChevronDown className="w-5 h-5 text-gray-500" />
                    : <ChevronRight className="w-5 h-5 text-gray-500" />
                  }
                </button>
                {expandedListId === list.id && (
                  <div className="px-4 pb-4">
                    <div className="flex flex-wrap gap-1.5">
                      {list.word_preview.map((word, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-700 rounded text-sm text-gray-200">
                          {word}
                        </span>
                      ))}
                      {list.word_count > list.word_preview.length && (
                        <span className="px-2 py-0.5 text-sm text-gray-500">
                          +{list.word_count - list.word_preview.length} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
